import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Tabs,
    Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/es';
import { RiCloseLine } from '@remixicon/react';
import { useAboutMe, useIdDestinatarioByUserLogged } from '@/shared/hooks/useConfiguracion.hook';
import { useUserData } from '@/shared/hooks/useUserData.hook';
import type { PerfilEntidad } from '@/shared/types/interfaces/user.interface';
import { storage } from '@/shared/utils/Const.utils';
import { StorageKeys } from '@/shared/types/enums/Storage.enum';
import type { TipoUsuarioPerfilOpcionEnum } from '@/modules/escritorio/types/enums/TipoUsuario.enum';
import { useIdUsuarioByTipoPersona } from '@/shared/hooks/useOrigenFuenteByTipoPersona.hook';
import PersonalConfig from './Personal/Personal';
import NotificacionesConfig from './Notificaciones/Notificaciones';
import TwoFactorAuthenticator from './TwoFactorAuthenticator/TwoFactorAuthenticator';
import DocumentosPersonales from './DocumentosPersonales/DocumentosPersonales';
import { useConfirmation } from '@/shared/hooks/useConfirmation.hook';
import PersonalForm from './Personal/PersonalForm';
import PersonalSkeleton from './Personal/PersonalSkeleton';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function a11yProps(index: number) {
    return {
        id: `config-tab-${index}`,
        'aria-controls': `config-tabpanel-${index}`,
    };
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`config-tabpanel-${index}`}
            aria-labelledby={`config-tab-${index}`}
            {...other}
            className='pt-4 h-[28.438rem] max-h-[29rem]'
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

interface ConfiguracionPerfilProps {
    open: boolean;
    onClose: () => void;
}

const ConfiguracionPerfil = ({
    open,
    onClose
}: ConfiguracionPerfilProps) => {
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const {userData} = useUserData();
    const idUsuario = userData?.id || 0;
    const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
    const opcionTipoUsuario = perfil?.opcion as TipoUsuarioPerfilOpcionEnum;

    let idDestinatario = useIdDestinatarioByUserLogged(opcionTipoUsuario);
    let idUsuarioSesion = useIdUsuarioByTipoPersona();

    const {
        confirmated: openForm,
        confirmation: isOpenForm,
        notConfirmated: closeForm
    } = useConfirmation({initialValue: false})

    const {
        data: dataAboutMe,
        isPending: isPendingAboutMe,
        error: errorAboutMe,
        isFetching: isFetchingAboutMe
    } = useAboutMe(idUsuario);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    /* console.log(userData) */
    /* console.log(dataAboutMe) */

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (!['backdropClick', 'escapeKeyDown'].includes(reason)) {
                    onClose();
                }
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: "rounded-2xl shadow-2xl"
            }}
            disableEscapeKeyDown
        >
            {/* Header */}
            <DialogTitle className="flex justify-between items-center pb-4 pt-6 px-6 border-b border-gray-100">
                <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                        {t('configuracionPerfil.title')}
                    </h2>
                </div>
                <IconButton
                    onClick={onClose}
                    size="small"
                    className="text-gray-500 hover:bg-gray-100 transition-colors"
                >
                    <RiCloseLine size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent className="px-6 pb-6 pt-4">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleChange} 
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="config tabs"
                    >
                        <Tab label={t('configuracionPerfil.personalInfo')} {...a11yProps(0)} />
                        <Tab label={t('configuracionPerfil.notifications')} {...a11yProps(1)} />
                        <Tab label={t('configuracionPerfil.twoFactorAuthenticator')} {...a11yProps(2)} /> 
                        <Tab label={t('configuracionPerfil.personalDocuments')} {...a11yProps(3)} />
                    </Tabs>
                </Box>
                <CustomTabPanel value={tabValue} index={0}>
                    {
                        isPendingAboutMe 
                        ? <PersonalSkeleton />
                        : !isOpenForm 
                        ? <PersonalConfig openForm={openForm} telefono={dataAboutMe?.Telefono || ''} correo={dataAboutMe?.Email || ''} />
                        : <PersonalForm
                            closeForm={closeForm}
                            datosUsuario={{
                                Celular: '',
                                Correo: dataAboutMe?.Email!, //|| userData?.email!,
                                Foto: dataAboutMe?.UrlFotoUsuario!, //|| userData?.image!,
                                IdUsuario: dataAboutMe?.IdUsuario!, //|| userData?.id!,
                                NombreFoto: userData?.fullName!,
                                Telefono: dataAboutMe?.Telefono! //|| userData?.telefono!
                            }}
                        />
                    }
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={1}>
                    <NotificacionesConfig idUsuario={idUsuario} idDestinatario={idDestinatario} idUsuarioSesion={idUsuarioSesion} onClose={onClose}/>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={2}>
                    <TwoFactorAuthenticator isActiveTwoFA={userData?.esActivo2FA!} onClose={onClose}/>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={3}>
                    <DocumentosPersonales />
                </CustomTabPanel>
            </DialogContent>
        </Dialog>
    );
};

export default ConfiguracionPerfil;